import { Cacheable, ICache, MaybePromise, WrappedCacheEntry } from '@auth0/auth0-spa-js';

import { InjectionToken } from '@angular/core';

import { devLog } from './jwt.constant';

const CACHE_KEY_PREFIX = '@@auth0spajs@@';

export const SESSION_STORAGE_CACHE = new InjectionToken<SessionStorageCache>('SESSION_STORAGE_CACHE');

/**
 * Custom Auth0 cache that keeps access tokens in memory (XSS-safe) while
 * persisting refresh token entries to sessionStorage so they survive page
 * refreshes within the same tab.
 *
 * On set(): if the entry contains a refresh_token, it is written to both
 * memory and sessionStorage. All other entries are kept in memory only.
 *
 * On get(): memory is checked first. On a page refresh memory is empty, so
 * the fallback to sessionStorage allows Auth0 to find the refresh token entry
 * and silently re-authenticate without a redirect.
 */
export class SessionStorageCache implements ICache {
    private readonly memory: Record<string, unknown> = {};

    set<T = Cacheable>(key: string, entry: T): void {
        this.memory[key] = entry;

        if (this.hasRefreshToken(entry)) {
            devLog('[SessionStorageCache] persisting new refresh token to sessionStorage'); // NOSONAR
            try {
                sessionStorage.setItem(key, JSON.stringify(this.stripSensitiveTokens(entry as unknown as WrappedCacheEntry)));
            } catch {
                // sessionStorage unavailable (private browsing quota etc.) — memory only
            }
        }
    }

    private stripSensitiveTokens(entry: WrappedCacheEntry): WrappedCacheEntry {
        const safeBody = { ...entry.body };
        delete safeBody.access_token;
        delete safeBody.id_token;
        delete safeBody.decodedToken;
        return { ...entry, body: safeBody };
    }

    get<T = Cacheable>(key: string): MaybePromise<T | undefined> {
        if (this.memory[key] !== undefined) {
            return this.memory[key] as T;
        }

        // Page refresh: memory is empty, fall back to sessionStorage.
        // Do NOT restore into memory: the persisted entry has no access_token,
        // so Auth0 will call set() with the full entry after a silent refresh.
        // Caching the stripped entry would cause Auth0 to see a missing
        // access_token on every get() and trigger concurrent refresh requests,
        // which causes 403s due to refresh token rotation consuming the token twice.
        try {
            const raw = sessionStorage.getItem(key);
            if (raw) {
                return JSON.parse(raw) as T;
            }
        } catch {
            // sessionStorage unavailable or JSON parse failed
        }

        return undefined;
    }

    remove(key: string): void {
        delete this.memory[key];
        try {
            sessionStorage.removeItem(key);
        } catch {
            // ignore
        }
    }

    allKeys(): string[] {
        const memoryKeys = Object.keys(this.memory).filter((k) => k.startsWith(CACHE_KEY_PREFIX));

        // On page refresh memory is empty — return keys from sessionStorage
        if (memoryKeys.length > 0) {
            return memoryKeys;
        }

        try {
            return Object.keys(sessionStorage).filter((k) => k.startsWith(CACHE_KEY_PREFIX));
        } catch {
            return [];
        }
    }

    clearAll(): void {
        for (const key of this.allKeys()) {
            this.remove(key);
        }
        devLog('[SessionStorageCache] cleared all entries'); // NOSONAR
    }

    private hasRefreshToken(entry: unknown): boolean {
        return !!(entry as WrappedCacheEntry)?.body?.refresh_token;
    }
}
